import { motion } from 'framer-motion';
import { Loader2, Radio, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTenantContext } from '../context/TenantContext';
import { resolveTenant as apiResolveTenant, updateTenantStatus, listGenres, updateTenantGenres } from '../services/api';
import type { GenreGroup } from '../services/api';
import { buildTenantUrl } from '../hooks/useTenant';
import { useAuthStore } from '../store/authStore';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import ArtistBanner from '../components/dashboard/ArtistBanner';
import ArtistAbout from '../components/dashboard/ArtistAbout';
import ArtistPromotedContent from '../components/dashboard/ArtistPromotedContent';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import ArtistProfileContentEditor from '../components/dashboard/ArtistProfileContentEditor';
import OnboardingProgressHero from '../components/dashboard/OnboardingProgressHero';

interface TenantData {
  id: number;
  slug: string;
  artistName: string;
  artistNumber?: number | null;
  primaryGenreId?: number | null;
  additionalGenreIds?: number[];
  selectedGenres?: Array<{ id: number; groupId: number; name: string; slug: string; isPrimary: boolean }>;
  ownerName: string;
  ownerEmail: string;
  createdAt: number;
  bannerUrl: string | null;
  bannerSrcset: Record<string, string> | null;
  profileUrl: string | null;
  statusText: string | null;
  pendingContent: string | null;
  publishedContent: string | null;
}

/**
 * Artist Dashboard Page — The artist's primary landing page.
 * Features a configurable banner, profile image, and promoted content.
 */
export default function ArtistPage({ slug: slugProp }: { slug?: string } = {}) {
  const tenant = useTenantContext();
  const slug = slugProp || (tenant.isTenant ? tenant.slug : '');

  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [genreGroups, setGenreGroups] = useState<GenreGroup[]>([]);
  const [genreSaving, setGenreSaving] = useState(false);
  const [genreError, setGenreError] = useState('');
  const [genresEditorOpen, setGenresEditorOpen] = useState(false);
  const { token } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);

  useEffect(() => {
    if (!slug) return;

    async function resolveTenant() {
      try {
        const res = await apiResolveTenant(slug);
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        const json = await res.json() as { data: TenantData };
        setTenantData(json.data);
        setStatusText(json.data.statusText || '');
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    resolveTenant();
  }, [slug]);

  useEffect(() => {
    async function loadGenres() {
      try {
        const res = await listGenres() as unknown as Response;
        if (!res.ok) return;
        const json = await res.json() as { data: GenreGroup[] };
        setGenreGroups(json.data || []);
      } catch {
        // non-fatal
      }
    }
    loadGenres();
  }, []);

  const handleBannerUpdate = useCallback((newUrl: string, srcset: Record<string, string> | null) => {
    setTenantData((prev) => prev ? { ...prev, bannerUrl: newUrl, bannerSrcset: srcset } : null);
  }, []);

  const handleStatusSubmit = useCallback(async () => {
    if (!slug) return;
    const trimmed = statusText.trim().slice(0, 50);
    setStatusSaving(true);
    try {
      const res = await updateTenantStatus(slug, trimmed || null, token);
      if (res.ok) {
        setStatusText(trimmed);
        setTenantData((prev) => prev ? { ...prev, statusText: trimmed || null } : null);
      }
    } catch (err) {
      console.warn('[Status] Failed to update:', err);
    } finally {
      setStatusSaving(false);
    }
  }, [slug, statusText, token]);

  const selectedGenres = tenantData?.selectedGenres || [];
  const selectedGenreIds = selectedGenres.map((g) => g.id);

  const handleToggleAdditionalGenre = useCallback(async (genreId: number) => {
    if (!tenantData || !slug) return;
    const isSelected = selectedGenreIds.includes(genreId);
    const next = isSelected
      ? selectedGenreIds.filter((id) => id !== genreId)
      : [...selectedGenreIds, genreId];

    if (!isSelected && next.length > 5) {
      setGenreError('You can select up to 5 genres.');
      return;
    }

    setGenreSaving(true);
    setGenreError('');
    try {
      const res = await updateTenantGenres(slug, next, token);
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        setGenreError(err.error || 'Failed to update genres.');
        return;
      }

      const allGenres = genreGroups.flatMap((g) => g.genres);
      const nextSelected = next
        .map((id) => allGenres.find((g) => g.id === id))
        .filter((g): g is NonNullable<typeof g> => !!g)
        .map((g) => ({
          id: g.id,
          groupId: g.groupId,
          name: g.name,
          slug: g.slug,
          isPrimary: false,
        }));

      setTenantData((prev) => prev ? {
        ...prev,
        primaryGenreId: next[0] ?? null,
        additionalGenreIds: next.slice(1),
        selectedGenres: nextSelected,
      } : prev);
    } catch {
      setGenreError('Failed to update genres.');
    } finally {
      setGenreSaving(false);
    }
  }, [tenantData, slug, selectedGenreIds, token, genreGroups]);

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-3">Tenant Not Found</h1>
          <p className="text-text-secondary font-mono">
            <span className="text-electric-cyan">{buildTenantUrl(slug)}</span> is not registered.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="dashboard" onboardingProgress={onboardingProgress} />

      {/* Main content */}
      <main className="pt-14">
        {/* Banner Section */}
        <ArtistBanner
          artistName={tenantData?.artistName || ''}
          artistNumber={tenantData?.artistNumber ?? null}
          bannerUrl={tenantData?.bannerUrl || null}
          bannerSrcset={tenantData?.bannerSrcset || null}
          slug={slug}
          onBannerUpdate={handleBannerUpdate}
        />

        {/* Profile & Promoted Content sections below banner */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Onboarding Progress Hero */}
          <OnboardingProgressHero progress={onboardingProgress} />

          {/* ─── Status Section ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center gap-4">
              <Radio className="w-8 h-8 text-green-400 flex-shrink-0" />
              <input
                type="text"
                value={statusText}
                onChange={(e) => setStatusText(e.target.value.slice(0, 50))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleStatusSubmit();
                  }
                }}
                maxLength={50}
                placeholder="Set a short public status visible on your profile..."
                disabled={statusSaving}
                className="w-full text-lg bg-transparent border-none outline-none text-white placeholder:text-white/20 font-mono disabled:opacity-50"
              />
              <span className="text-xs text-text-muted font-mono flex-shrink-0">
                {statusText.length}/50
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8"
          >
            <div className="glass-card rounded-2xl p-5 border border-white/10">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-mono uppercase tracking-wider text-electric-cyan">Genres</h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-text-muted font-mono">{selectedGenres.length}/5 selected</span>
                  <button
                    type="button"
                    onClick={() => setGenresEditorOpen((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/15 text-xs text-white/85 hover:text-white hover:border-white/35 transition-colors"
                  >
                    {genresEditorOpen ? (
                      <>
                        <ChevronUp className="w-3.5 h-3.5" />
                        Collapse
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3.5 h-3.5" />
                        Edit
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {selectedGenres.length > 0 ? selectedGenres.map((genre) => (
                  <span
                    key={genre.id}
                    className="px-3 py-1 rounded-full text-xs font-mono border bg-white/5 border-white/15 text-white/90"
                  >
                    {genre.name}
                  </span>
                )) : (
                  <span className="text-xs text-text-muted">No genres configured.</span>
                )}
              </div>

              {genresEditorOpen && (
                <>
                  <p className="text-xs text-text-muted mb-3">
                    Select up to 5 genres.
                  </p>

                  <div className="space-y-3 max-h-56 overflow-auto pr-1">
                    {genreGroups.map((group) => (
                      <div key={group.id}>
                        <p className="text-[11px] uppercase tracking-wider text-white/60 mb-2">{group.name}</p>
                        <div className="flex flex-wrap gap-2">
                          {group.genres.map((genre) => {
                            const isSelected = selectedGenreIds.includes(genre.id);
                            return (
                              <button
                                key={genre.id}
                                type="button"
                                disabled={genreSaving}
                                onClick={() => handleToggleAdditionalGenre(genre.id)}
                                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${isSelected ? 'bg-white/10 border-white/40 text-white' : 'bg-transparent border-white/20 text-white/75 hover:text-white hover:border-white/40'} ${genreSaving ? 'opacity-70' : ''}`}
                              >
                                {genre.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {genreError && <p className="text-xs text-red-400 mt-3">{genreError}</p>}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="flex flex-col lg:flex-row gap-8"
          >
            {/* Profile Image Section — Left */}
            <ArtistAbout
              profileImageUrl={tenantData?.profileUrl || null}
              slug={slug}
              onProfileImageUpdate={(url) => {
                setTenantData((prev) => prev ? { ...prev, profileUrl: url } : null);
                onboardingProgress.refresh();
              }}
            />

            {/* Promoted Content Section — Right */}
            <ArtistPromotedContent slug={slug} />
          </motion.div>

          {/* Profile Content Editor — Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8"
          >
            <ArtistProfileContentEditor
              slug={slug}
              initialContent={tenantData?.pendingContent || null}
              publishedContent={tenantData?.publishedContent || null}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
}
